import React, { useRef, useState } from "react";
import { Paperclip, FileCode2, FileSpreadsheet, FileText, Image as ImageIcon, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { API_BASE, authFetch } from "@/lib/api";
import { TID } from "@/lib/testIds";

const ACCEPT = ".abap,.txt,.md,.json,.xml,.edmx,.csv,.sql,.log,.yaml,.yml,.sap,.html,.js,.py,.xlsx,.xls,.xlsm,.pdf,.docx,.png,.jpg,.jpeg,.webp,.bmp";

const KIND_ICON = {
    abap: FileCode2,
    text: FileCode2,
    spreadsheet: FileSpreadsheet,
    pdf: FileText,
    document: FileText,
    image: ImageIcon,
};

const KIND_TONE = {
    abap: "text-rose-300 border-rose-500/30 bg-rose-500/10",
    text: "text-slate-300 border-white/15 bg-white/5",
    spreadsheet: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10",
    pdf: "text-amber-300 border-amber-500/30 bg-amber-500/10",
    document: "text-amber-300 border-amber-500/30 bg-amber-500/10",
    image: "text-cyan-300 border-cyan-500/30 bg-cyan-500/10",
};

const prettyBytes = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

/** Attach files to a turn of the conversation. Each file is parsed server-side
 * once (attachments.py) and referenced by id on the next message, so a long
 * conversation never re-parses the same PDF. */
export function AttachmentComposer({ attachments, setAttachments, sessionId, disabled, children }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(0);

    const upload = async (files) => {
        for (const file of files) {
            setUploading((n) => n + 1);
            try {
                const form = new FormData();
                form.append("file", file);
                if (sessionId) form.append("session_id", sessionId);
                const res = await authFetch(`${API_BASE}/utsav/attachments`, { method: "POST", body: form });
                const data = await res.json().catch(() => ({}));
                if (!res.ok) throw new Error(data.detail || "Upload failed");
                setAttachments((prev) => [...prev, data]);
                toast.success(`${data.filename} attached`, { description: data.summary });
            } catch (e) {
                toast.error(`Could not attach ${file.name}`, { description: String(e.message || e) });
            } finally {
                setUploading((n) => n - 1);
            }
        }
    };

    const remove = async (id) => {
        setAttachments((prev) => prev.filter((a) => a.id !== id));
        try {
            await authFetch(`${API_BASE}/utsav/attachments/${id}`, { method: "DELETE" });
        } catch (e) {
            /* the chip is already gone from the composer — server cleanup is best-effort */
        }
    };

    return (
        <div className="space-y-2">
            {(attachments.length > 0 || uploading > 0) && (
                <div className="flex flex-wrap gap-1.5" data-testid="utsav-attachment-tray">
                    {attachments.map((a) => {
                        const Icon = KIND_ICON[a.kind] || FileText;
                        return (
                            <span
                                key={a.id}
                                data-testid={TID.utsavAttachmentChip(a.id)}
                                title={a.summary}
                                className={`inline-flex items-center gap-1.5 pl-2 pr-1.5 py-1 rounded-sm border text-[11px] font-mono ${KIND_TONE[a.kind] || KIND_TONE.text}`}
                            >
                                <Icon size={11} />
                                <span className="max-w-[190px] truncate">{a.filename}</span>
                                <span className="text-slate-500">{prettyBytes(a.size)}</span>
                                {a.truncated && <span className="text-amber-400" title="Only the first part fits in context">clipped</span>}
                                <button
                                    data-testid={TID.utsavRemoveAttachment(a.id)}
                                    onClick={() => remove(a.id)}
                                    className="text-slate-500 hover:text-rose-400 transition-colors"
                                    aria-label={`Remove ${a.filename}`}
                                >
                                    <X size={11} />
                                </button>
                            </span>
                        );
                    })}
                    {uploading > 0 && (
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-white/10 bg-white/5 text-[11px] font-mono text-slate-400">
                            <Loader2 size={11} className="animate-spin" /> parsing {uploading} file{uploading > 1 ? "s" : ""}…
                        </span>
                    )}
                </div>
            )}
            <input
                ref={inputRef}
                data-testid={TID.utsavAttachInput}
                type="file"
                multiple
                accept={ACCEPT}
                className="hidden"
                onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    e.target.value = "";
                    if (files.length) upload(files);
                }}
            />
            <div className="flex items-end gap-2">
                <button
                    data-testid={TID.utsavAttachBtn}
                    onClick={() => inputRef.current?.click()}
                    disabled={disabled}
                    title="Attach ABAP, spreadsheets, PDF, Word or images"
                    className="p-3 rounded-sm border border-white/10 text-slate-400 hover:text-cyan-300 hover:border-cyan-500/40 transition-colors shrink-0 disabled:opacity-40"
                >
                    <Paperclip size={16} />
                </button>
                {children}
            </div>
        </div>
    );
}

/** Read-only chips shown on a user message that carried attachments. */
export function AttachmentBadges({ attachments }) {
    if (!attachments?.length) return null;
    return (
        <div className="flex flex-wrap gap-1.5 mb-2">
            {attachments.map((a) => {
                const Icon = KIND_ICON[a.kind] || FileText;
                return (
                    <span
                        key={a.id || a.filename}
                        title={a.summary}
                        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm border text-[10px] font-mono ${KIND_TONE[a.kind] || KIND_TONE.text}`}
                    >
                        <Icon size={10} /> {a.filename}
                    </span>
                );
            })}
        </div>
    );
}
